import { useLogger } from "@sg-protect/federation-runtime/squide";
import { type ErrorInfo, type PropsWithChildren, type ReactNode, useCallback, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useLocation } from "react-router";

export interface ProtectSearchResult {
    id: string;
    displayName: string;
    to: string;
    trackingTrigger: string;
    icon?: ReactNode;
    description?: ReactNode;
}

export interface ProtectRecentlyViewedEntry {
    providerId: string;
    id: string;
    displayName: string;
    /**
     * Provider-owned payload. The shell never reads it, but it round-trips through `JSON.stringify` into
     * localStorage, so keep it to plain JSON: no Date, Map, Set or class instance, and write `?? null` rather
     * than leaving a key undefined. Anything else comes back a different shape, the provider's own guard rejects it
     * in `useRecentResults`, and the row disappears while still holding one of that provider's slots.
     */
    data: unknown;
    viewedAt: number;
}

export type ProtectRecentlyViewedInput = Omit<ProtectRecentlyViewedEntry, "viewedAt">;

export interface ProtectSearchProviderResults {
    /** `undefined` means no answer yet, which keeps the palette on its "Searching…" status. */
    results: ProtectSearchResult[] | undefined;
    isFetching: boolean;
    isError: boolean;
    warning?: ReactNode;
}

export interface ProtectSearchProviderOptions {
    id: string;
    priority: number;
    minQueryLength: number;
    /** Plural nouns for what this provider returns ("workspaces", "OneDrives"), woven into the shell's search copy. */
    entityLabels: string[];
    /** Rendered verbatim into the section header, so carry `data-public` on a literal leaf here. */
    sectionLabel: ReactNode;
    recentSectionLabel: ReactNode;
    useResults: (query: string, isEnabled: boolean) => ProtectSearchProviderResults;
    useRecentResults?: (entries: ProtectRecentlyViewedEntry[]) => ProtectSearchResult[];
    /** Observes the route and returns the entry to record, or undefined when the route isn't the provider's. */
    useVisitedEntry?: () => ProtectRecentlyViewedInput | undefined;
}

// Not reactive: read feature flags inside the provider's hooks, since a provider registered or skipped here never changes.
const providers = new Map<string, ProtectSearchProviderOptions>();

export function registerProtectSearchProvider(options: ProtectSearchProviderOptions) {
    providers.set(options.id, options);

    return () => {
        providers.delete(options.id);
    };
}

// Snapshot once: the palette calls one hook per provider, so the list must not change length while it is mounted.
export function useRegisteredSearchProviders() {
    const [snapshot] = useState(() => Array.from(providers.values()).toSorted((a, b) => b.priority - a.priority));

    return snapshot;
}

interface SearchProviderBoundaryProps extends PropsWithChildren {
    /** Retries on each change, on top of the route: a provider error should not outlive the palette session that hit it. */
    resetKeys?: unknown[];
    onError?: () => void;
}

// Other modules supply these hooks, so a crash in one must not blank the header.
export function SearchProviderBoundary({ children, resetKeys = [], onError }: SearchProviderBoundaryProps) {
    const logger = useLogger();
    const { pathname } = useLocation();

    const handleError = useCallback(
        (error: unknown, info: ErrorInfo) => {
            logger
                .withText("[shell] A search provider crashed and was hidden")
                .withError(error instanceof Error ? error : new Error(String(error)))
                .withObject(info)
                .error();
            onError?.();
        },
        [logger, onError]
    );

    return (
        <ErrorBoundary fallbackRender={() => null} onError={handleError} resetKeys={[pathname, ...resetKeys]}>
            {children}
        </ErrorBoundary>
    );
}
